import React, { useCallback, useMemo, useState } from "react";
import {
  ASSET_META,
  ASSET_STATUS_LABEL,
  DEMO_ASSETS,
  DEMO_MAINT_LOGS,
  LOG_STATUS_LABEL,
  LOG_TYPE_META,
} from "../data/operationsDemoData";
import "./styling/MaintenanceDemo.css";

/**
 * MaintenanceDemo — homepage marketing preview of the Maintenance Logs page.
 * Mirrors its two-pane shape (asset tree left, history right) over illustrative
 * assets, so the facility → area → device → part hierarchy and the roll-up
 * badges are visible without an account.
 */

const dateFromDaysAgo = (days) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return new Date(d.getTime() - days * 86400000).toISOString().slice(0, 10);
};

const MaintenanceDemo = () => {
  const [selectedId, setSelectedId] = useState("irr");
  const [includeSubs, setIncludeSubs] = useState(true);

  const childrenByParent = useMemo(() => {
    const map = new Map();
    DEMO_ASSETS.forEach((a) => {
      if (!a.parent) return;
      if (!map.has(a.parent)) map.set(a.parent, []);
      map.get(a.parent).push(a.id);
    });
    return map;
  }, []);

  /** Every descendant of an asset, plus the asset itself. */
  const subtreeOf = useCallback(
    (id) => {
      const ids = new Set();
      const stack = [id];
      while (stack.length) {
        const current = stack.pop();
        if (ids.has(current)) continue;
        ids.add(current);
        (childrenByParent.get(current) || []).forEach((c) => stack.push(c));
      }
      return ids;
    },
    [childrenByParent]
  );

  /** Open issues roll up the subtree, so a collapsed branch still signals. */
  const openCounts = useMemo(() => {
    const counts = new Map();
    DEMO_ASSETS.forEach((a) => {
      const ids = subtreeOf(a.id);
      const n = DEMO_MAINT_LOGS.filter(
        (l) => ids.has(l.assetId) && l.status !== "resolved"
      ).length;
      if (n) counts.set(a.id, n);
    });
    return counts;
  }, [subtreeOf]);

  const visibleLogs = useMemo(() => {
    const ids = includeSubs ? subtreeOf(selectedId) : new Set([selectedId]);
    return DEMO_MAINT_LOGS.filter((l) => ids.has(l.assetId));
  }, [selectedId, includeSubs, subtreeOf]);

  const selected = DEMO_ASSETS.find((a) => a.id === selectedId);
  const assetName = (id) => DEMO_ASSETS.find((a) => a.id === id)?.name || "";

  return (
    <div className="maint-demo">
      <div className="maint-demo__tree" role="tree" aria-label="Asset hierarchy">
        <p className="maint-demo__pane-title">Assets</p>
        {DEMO_ASSETS.map((a) => {
          const open = openCounts.get(a.id);
          return (
            <button
              key={a.id}
              type="button"
              role="treeitem"
              aria-selected={a.id === selectedId}
              className={`maint-demo__node${a.id === selectedId ? " is-selected" : ""}`}
              style={{ paddingLeft: `${0.45 + a.depth * 0.8}rem` }}
              onClick={() => setSelectedId(a.id)}
            >
              <span className={`maint-demo__dot maint-demo__dot--${a.status}`} />
              <span className="maint-demo__node-icon" aria-hidden="true">
                {ASSET_META[a.type].icon}
              </span>
              <span className="maint-demo__node-name">{a.name}</span>
              {open ? (
                <span
                  className="maint-demo__badge"
                  title={`${open} open issue${open > 1 ? "s" : ""} in this branch`}
                >
                  {open}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="maint-demo__detail">
        <div className="maint-demo__detail-head">
          <div>
            <p className="maint-demo__pane-title">History</p>
            <p className="maint-demo__selected">
              <span aria-hidden="true">{ASSET_META[selected.type].icon}</span>{" "}
              {selected.name}
              <span className={`maint-demo__pill maint-demo__pill--${selected.status}`}>
                {ASSET_STATUS_LABEL[selected.status]}
              </span>
            </p>
          </div>
          <label className="maint-demo__check">
            <input
              type="checkbox"
              checked={includeSubs}
              onChange={(e) => setIncludeSubs(e.target.checked)}
            />
            Include sub-assets
          </label>
        </div>

        <div className="maint-demo__logs">
          {visibleLogs.length === 0 ? (
            <p className="maint-demo__empty">
              No entries logged against this asset yet.
            </p>
          ) : (
            visibleLogs.map((l) => (
              <article key={l.id} className="maint-demo__log">
                <header className="maint-demo__log-head">
                  <span className="maint-demo__log-type">
                    <span aria-hidden="true">{LOG_TYPE_META[l.type].icon}</span>{" "}
                    {LOG_TYPE_META[l.type].label}
                  </span>
                  <span className={`maint-demo__log-status maint-demo__log-status--${l.status}`}>
                    {LOG_STATUS_LABEL[l.status]}
                  </span>
                  <time className="maint-demo__log-date">{dateFromDaysAgo(l.days)}</time>
                </header>
                <p className="maint-demo__log-title">{l.title}</p>
                <p className="maint-demo__log-asset">{assetName(l.assetId)}</p>
                <p className="maint-demo__log-meta">
                  {l.by && <span>👤 {l.by}</span>}
                  {l.downtime != null && <span>⏱️ {l.downtime} h downtime</span>}
                  {l.cost != null && <span>💰 {l.cost.toLocaleString()}</span>}
                  {l.parts && <span>🔩 {l.parts}</span>}
                </p>
              </article>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceDemo;
